import { useRef, useState } from 'react'
import { ExternalLink, FileText, Loader2, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useMockApi } from '@/lib/useMock'
import {
  getMockPreviewKind,
  getMockReleaseScenario,
  setMockPreviewKind,
  setMockReleaseScenario,
  type LisPdfReport,
  type LisReleaseDocument,
  type LisResultReport,
  type LisResultRelease,
  type LisResultStatusCode,
} from '@/api/lisResultsApi'
import {
  useLisPdfPreview,
  useLisPdfReports,
  useLisReleasePreview,
  useLisResultReports,
  useReleaseLisResults,
  useRetryLisResultRelease,
  useSendLisPdf,
} from '@/hooks/useLisResults'
import { ABNORMAL_FLAGS, computeFlag } from '@/lib/lis/resultMapping'
import {
  MOCK_PREVIEW_KINDS,
  MOCK_RELEASE_SCENARIOS,
  type MockPreviewKind,
  type MockReleaseScenario,
} from '@/lib/lis/releaseMock'
import {
  RESULT_STATUS_LABEL,
  isOpenRelease,
  releaseDestinationNote,
  releaseStatusView,
  type ReleaseStatusView,
} from '@/lib/lis/releaseStatus'
import { pdfPreviewView, releasedFileLabel } from '@/lib/lis/pdfMapping'
import { orderingProviderLine } from '@/lib/lis/orderingProvider'
import { releaseClaim, tryClaim } from '@/lib/lis/inFlight'
import type { LisOrderingProvider } from '@/api/lisResultsApi'

/**
 * Send this product's Title21-entered results (z8nrz7ffzv) and PDFs
 * (z8nrz7fgcp) to Epic. Both go UNSOLICITED (MedStar): nothing is ordered
 * first; Title21 mints the order number when it sends, under the mapping's
 * order code. One card per outgoing mapping whose form is on the product's
 * procedure, offered once that form is completed.
 *
 * The confirmation lists exactly what will be sent, and where; the destination
 * comes from the LIS integration that owns the mapping, never from this screen.
 */
export function LisReleasePanel({ productId }: { productId: string }) {
  const results = useLisResultReports(productId)
  const pdfs = useLisPdfReports(productId)
  const nothing =
    results.isSuccess && pdfs.isSuccess && (results.data ?? []).length === 0 && (pdfs.data ?? []).length === 0
  return (
    <div className='space-y-3'>
      {useMockApi ? <MockScenarioControl /> : null}
      {results.isPending || pdfs.isPending ? <p className='text-sm text-muted-foreground'>Loading…</p> : null}
      {results.isError ? <p className='text-sm text-destructive'>Could not load the results to send.</p> : null}
      {nothing ? (
        <p className='rounded-xl border bg-white p-4 text-sm text-muted-foreground shadow-sm'>
          Nothing to send to Epic for this product. Results and PDFs appear here once an outgoing mapping
          (Integrations → the LIS integration → Mapping → Outgoing) names a form this product&apos;s procedure has.
        </p>
      ) : null}
      {(results.data ?? []).map((r) => (
        <ResultReportCard key={r.mappingId} productId={productId} report={r} />
      ))}
      <PdfReports productId={productId} />
    </div>
  )
}

/** Dev only: which canned answer the in-app mock gives next (ffzv AC3.2, fgcp AC3.1). */
function MockScenarioControl() {
  const [scenario, setScenario] = useState<MockReleaseScenario>(getMockReleaseScenario())
  const [kind, setKind] = useState<MockPreviewKind>(getMockPreviewKind())
  return (
    <div className='flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs'>
      <span className='font-medium text-amber-900'>Mock (VITE_USE_MOCK): next answer</span>
      <Select
        value={scenario}
        onValueChange={(v) => {
          setScenario(v as MockReleaseScenario)
          setMockReleaseScenario(v as MockReleaseScenario)
        }}
      >
        <SelectTrigger className='h-7 w-56 bg-white text-xs' aria-label='Mock release answer'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MOCK_RELEASE_SCENARIOS.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className='font-medium text-amber-900'>PDF</span>
      <Select
        value={kind}
        onValueChange={(v) => {
          setKind(v as MockPreviewKind)
          setMockPreviewKind(v as MockPreviewKind)
        }}
      >
        <SelectTrigger className='h-7 w-56 bg-white text-xs' aria-label='Mock release content'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MOCK_PREVIEW_KINDS.map((k) => (
            <SelectItem key={k.value} value={k.value}>
              {k.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ResultReportCard({ productId, report }: { productId: string; report: LisResultReport }) {
  const [confirming, setConfirming] = useState(false)
  const retry = useRetryLisResultRelease()
  const retryLock = useRef(false)
  const latest = report.releases[0] ?? null
  // A release that is still open is sent again, not released anew: a second
  // one could file the same results twice.
  const open = latest && isOpenRelease(latest.status)
  const completed = report.submission.status === 'completed'

  return (
    <div className='rounded-xl border bg-white shadow-sm'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3'>
        <span className='font-medium'>
          {report.testCode}
          {report.testName ? ` — ${report.testName}` : ''}
        </span>
        <span className='text-xs text-muted-foreground'>
          Results · {completed ? 'form completed' : 'form not completed yet'}
        </span>
        <div className='ml-auto'>
          <Button size='sm' onClick={() => setConfirming(true)} disabled={!!open || !completed}>
            <Send className='mr-1 h-3.5 w-3.5' />
            Release to Epic
          </Button>
        </div>
      </div>

      {latest ? (
        <div className='border-t px-4 py-3'>
          <ReleaseStatus
            view={releaseStatusView(latest)}
            pending={retry.isPending}
            onAction={() => {
              if (!tryClaim(retryLock)) return
              retry.mutate(latest.id, { onSettled: () => releaseClaim(retryLock) })
            }}
          />
          {retry.error ? <p className='mt-1 text-sm text-destructive'>{retry.error.message}</p> : null}
        </div>
      ) : null}

      {report.releases.length > 0 ? <ReleaseHistory releases={report.releases} /> : null}

      {confirming ? (
        <ReleaseDialog productId={productId} report={report} onClose={() => setConfirming(false)} />
      ) : null}
    </div>
  )
}

const TONE: Record<ReleaseStatusView['tone'], string> = {
  success: 'text-green-700',
  error: 'text-destructive',
  warning: 'text-amber-700',
  muted: 'text-muted-foreground',
}

function ReleaseStatus({
  view,
  pending,
  onAction,
}: {
  view: ReleaseStatusView
  pending: boolean
  onAction: () => void
}) {
  return (
    <div className='flex flex-wrap items-center gap-3'>
      <span className={cn('text-sm font-medium', TONE[view.tone])}>{view.text}</span>
      {view.action ? (
        // Disabled while in flight, so a double click sends one request.
        <Button size='sm' variant='outline' onClick={onAction} disabled={pending}>
          {pending ? <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' /> : null}
          {view.action === 'retry' ? 'Retry' : 'Send again'}
        </Button>
      ) : null}
    </div>
  )
}

/** Who released what, when, F or C, to where, and how it ended (ffzv AC3.7); for a PDF, which file (fgcp AC2.4). */
function ReleaseHistory({ releases }: { releases: LisResultRelease[] }) {
  const anyFile = releases.some((r) => releasedFileLabel(r.payload))
  return (
    <div className='overflow-x-persistent border-t'>
      <table className='w-full text-xs'>
        <thead className='bg-muted/40 text-left text-muted-foreground'>
          <tr>
            <th className='px-4 py-1.5'>Released</th>
            <th className='px-4 py-1.5'>By</th>
            <th className='px-4 py-1.5'>Status</th>
            {anyFile ? <th className='px-4 py-1.5'>File</th> : null}
            <th className='px-4 py-1.5'>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {releases.map((r) => (
            <tr key={r.id} className='border-t'>
              <td className='px-4 py-1.5'>{new Date(r.releasedAt).toLocaleString()}</td>
              <td className='px-4 py-1.5'>{r.releasedBy}</td>
              <td className='px-4 py-1.5'>{RESULT_STATUS_LABEL[r.resultStatus] ?? r.resultStatus}</td>
              {anyFile ? <td className='px-4 py-1.5'>{releasedFileLabel(r.payload) ?? '—'}</td> : null}
              <td className='px-4 py-1.5'>{releaseStatusView(r).text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The confirmation: every observation that will go out, and where. Nothing is
 * sent on Cancel. Refusals (no mapping, no value, not a number) come back from
 * the preview with the backend's message, before anything can be sent.
 */
function ReleaseDialog({
  productId,
  report,
  onClose,
}: {
  productId: string
  report: LisResultReport
  onClose: () => void
}) {
  const [resultStatus, setResultStatus] = useState<LisResultStatusCode>('F')
  const [flags, setFlags] = useState<Record<string, string>>({})
  // Flags are chosen on screen, so only the result status asks the backend again.
  const preview = useLisReleasePreview({ mappingId: report.mappingId, productId, resultStatus })
  const release = useReleaseLisResults()
  const sendLock = useRef(false)
  const [sent, setSent] = useState<LisResultRelease | null>(null)

  const entered = new Set(preview.data?.enteredFlagCodes ?? [])

  return (
    <Dialog open onOpenChange={(open) => !open && !release.isPending && onClose()}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Release {report.testCode} results to Epic</DialogTitle>
          <DialogDescription>Sent on its own: Title21 makes the order number. Nothing needs ordering first.</DialogDescription>
        </DialogHeader>

        {sent ? (
          // No Retry here: it would only close the dialog. The card's status row
          // under the dialog sends it again.
          <ReleaseStatus view={{ ...releaseStatusView(sent), action: null }} pending={false} onAction={onClose} />
        ) : null}
        {/* Outside the preview: a refusal (e.g. C with no final yet) must leave the status changeable. */}
        {sent ? null : <ResultStatusSelect id='lis-release-status' value={resultStatus} onChange={setResultStatus} />}
        {sent ? null : preview.isPending ? (
          <p className='text-sm text-muted-foreground'>Building what will be sent…</p>
        ) : preview.isError ? (
          <p className='text-sm text-destructive'>{preview.error.message}</p>
        ) : (
          <div className='space-y-3'>
            <p className='text-sm'>
              {releaseDestinationNote(preview.data.destination)}{' '}
              <span className='text-muted-foreground'>(from {preview.data.integrationName})</span>
            </p>
            <ProviderLine provider={preview.data.orderingProvider} />
            <div className='overflow-x-persistent rounded-lg border'>
              <table className='w-full text-sm'>
                <thead className='bg-muted/40 text-left text-xs text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2'>Code</th>
                    <th className='px-3 py-2'>Name</th>
                    <th className='px-3 py-2'>Value</th>
                    <th className='px-3 py-2'>Units</th>
                    <th className='px-3 py-2'>Reference range</th>
                    <th className='px-3 py-2'>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.observations.map((o) => (
                    <tr key={o.code} className='border-t'>
                      <td className='px-3 py-2 font-mono'>{o.code}</td>
                      <td className='px-3 py-2'>{o.text ?? '—'}</td>
                      <td className='px-3 py-2 font-mono'>{o.value}</td>
                      <td className='px-3 py-2'>{o.units ?? '—'}</td>
                      <td className='px-3 py-2'>{o.referenceRange ?? '—'}</td>
                      <td className='px-3 py-2'>
                        {entered.has(o.code) ? (
                          <FlagSelect
                            code={o.code}
                            // Pre-selected from the range when there is one; the tech can change it.
                            value={flags[o.code] ?? computeFlag(o.value, o.referenceRange) ?? ''}
                            onChange={(v) => setFlags((f) => ({ ...f, [o.code]: v }))}
                          />
                        ) : (
                          (o.abnormalFlag ?? '—')
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {release.error ? <p className='text-sm text-destructive'>{release.error.message}</p> : null}

        <DialogFooter>
          {sent ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button variant='outline' onClick={onClose} disabled={release.isPending}>
                Cancel
              </Button>
              <Button
                disabled={!preview.isSuccess || release.isPending}
                onClick={() => {
                  if (!tryClaim(sendLock)) return
                  // Entered flags the tech left on the pre-selection still go out.
                  const chosen = Object.fromEntries(
                    (preview.data?.observations ?? [])
                      .filter((o) => entered.has(o.code))
                      .map((o) => [o.code, flags[o.code] ?? computeFlag(o.value, o.referenceRange) ?? ''])
                      .filter(([, v]) => v !== ''),
                  )
                  release.mutate(
                    { mappingId: report.mappingId, productId, resultStatus, flags: chosen },
                    { onSuccess: setSent, onSettled: () => releaseClaim(sendLock) },
                  )
                }}
              >
                {release.isPending ? <Loader2 className='mr-1 h-4 w-4 animate-spin' /> : null}
                Send to Epic
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** OBR-25: final, preliminary or corrected. A C needs an accepted F first; the backend says so. */
function ResultStatusSelect({
  id,
  value,
  onChange,
}: {
  id: string
  value: LisResultStatusCode
  onChange: (value: LisResultStatusCode) => void
}) {
  return (
    <div className='grid max-w-xs gap-1.5'>
      <Label htmlFor={id}>Result status</Label>
      <Select value={value} onValueChange={(v) => onChange(v as LisResultStatusCode)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(['F', 'P', 'C'] as const).map((s) => (
            <SelectItem key={s} value={s}>
              {s} — {RESULT_STATUS_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/** OBR-16 as it will go. The backend refuses the preview when there is none. */
function ProviderLine({ provider }: { provider: LisOrderingProvider | null | undefined }) {
  const line = orderingProviderLine(provider)
  return line ? (
    <p className='text-sm'>
      <span className='text-muted-foreground'>Ordering provider:</span> {line}
    </p>
  ) : null
}

const NO_FLAG = '__none__'

function FlagSelect({ code, value, onChange }: { code: string; value: string; onChange: (value: string) => void }) {
  return (
    <Select value={value || NO_FLAG} onValueChange={(v) => onChange(v === NO_FLAG ? '' : v)}>
      <SelectTrigger className='h-7 w-28 text-xs' aria-label={`Flag for ${code}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_FLAG}>No flag</SelectItem>
        {ABNORMAL_FLAGS.map((f) => (
          <SelectItem key={f} value={f}>
            {f}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** The PDF that will go (fgcp AC2.1), or why it cannot (AC2.2). */
function PdfPreview({
  document,
  details,
  problem,
}: {
  document: LisReleaseDocument
  details: string
  problem: string | null
}) {
  return (
    <div className={cn('rounded-lg border p-3', problem && 'border-destructive/40 bg-destructive/5')}>
      <div className='flex flex-wrap items-center gap-2'>
        <FileText className='h-4 w-4 text-muted-foreground' />
        <span className='text-sm font-medium'>{details}</span>
        {document.viewUrl ? (
          <a
            href={document.viewUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline'
          >
            Open PDF
            <ExternalLink className='h-3.5 w-3.5' />
          </a>
        ) : null}
      </div>
      <p className='mt-1 text-xs text-muted-foreground'>
        One result line: <span className='font-mono'>{document.code}</span>
        {document.text ? ` — ${document.text}` : ''} (type ED). The file is sent unchanged.
      </p>
      {problem ? <p className='mt-2 text-sm text-destructive'>{problem} It can&apos;t be sent.</p> : null}
    </div>
  )
}

/**
 * The PDFs this product can send to Epic (z8nrz7fgcp): one card per PDF
 * mapping whose form is on the product's procedure. MedStar: a PDF goes
 * unsolicited, so nothing needs ordering first; Send mints the order number.
 */
function PdfReports({ productId }: { productId: string }) {
  const reports = useLisPdfReports(productId)
  if (reports.isPending || reports.isError || (reports.data ?? []).length === 0) return null
  return (
    <>
      {(reports.data ?? []).map((r) => (
        <PdfReportCard key={r.mappingId} productId={productId} report={r} />
      ))}
    </>
  )
}

function PdfReportCard({ productId, report }: { productId: string; report: LisPdfReport }) {
  const [confirming, setConfirming] = useState(false)
  const retry = useRetryLisResultRelease()
  const retryLock = useRef(false)
  const latest = report.releases[0] ?? null
  // An open send is sent again, never sent anew: that could file the PDF twice.
  const open = latest && isOpenRelease(latest.status)
  const completed = report.submission.status === 'completed'

  return (
    <div className='rounded-xl border bg-white shadow-sm'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3'>
        <FileText className='h-4 w-4 text-muted-foreground' />
        <span className='font-medium'>
          {report.testCode}
          {report.testName ? ` — ${report.testName}` : ''}
        </span>
        <span className='text-xs text-muted-foreground'>PDF · {completed ? 'form completed' : 'form not completed yet'}</span>
        <div className='ml-auto'>
          <Button size='sm' onClick={() => setConfirming(true)} disabled={!!open || !completed}>
            <Send className='mr-1 h-3.5 w-3.5' />
            Send PDF to Epic
          </Button>
        </div>
      </div>

      {latest ? (
        <div className='border-t px-4 py-3'>
          <ReleaseStatus
            view={releaseStatusView(latest)}
            pending={retry.isPending}
            onAction={() => {
              if (!tryClaim(retryLock)) return
              retry.mutate(latest.id, { onSettled: () => releaseClaim(retryLock) })
            }}
          />
          {retry.error ? <p className='mt-1 text-sm text-destructive'>{retry.error.message}</p> : null}
        </div>
      ) : null}

      {report.releases.length > 0 ? <ReleaseHistory releases={report.releases} /> : null}

      {confirming ? (
        <PdfSendDialog productId={productId} report={report} onClose={() => setConfirming(false)} />
      ) : null}
    </div>
  )
}

/**
 * The confirmation for a PDF (fgcp AC2.1, AC2.2): the file, where it goes, a
 * link to open it. A file that cannot be sent says why, and Send stays off.
 */
function PdfSendDialog({ productId, report, onClose }: { productId: string; report: LisPdfReport; onClose: () => void }) {
  const [resultStatus, setResultStatus] = useState<LisResultStatusCode>('F')
  const preview = useLisPdfPreview({ mappingId: report.mappingId, productId, resultStatus })
  const send = useSendLisPdf()
  const sendLock = useRef(false)
  const [sent, setSent] = useState<LisResultRelease | null>(null)
  const pdf = preview.data ? pdfPreviewView(preview.data.document) : null

  return (
    <Dialog open onOpenChange={(open) => !open && !send.isPending && onClose()}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Send {report.testCode} PDF to Epic</DialogTitle>
          <DialogDescription>Sent on its own: Title21 makes the order number. Nothing needs ordering first.</DialogDescription>
        </DialogHeader>

        {sent ? (
          // No Retry here: it would only close the dialog. The card's status row
          // under the dialog sends it again.
          <ReleaseStatus view={{ ...releaseStatusView(sent), action: null }} pending={false} onAction={onClose} />
        ) : null}
        {sent ? null : <ResultStatusSelect id='lis-pdf-status' value={resultStatus} onChange={setResultStatus} />}
        {sent ? null : preview.isPending ? (
          <p className='text-sm text-muted-foreground'>Reading the PDF…</p>
        ) : preview.isError ? (
          <p className='text-sm text-destructive'>{preview.error.message}</p>
        ) : (
          <div className='space-y-3'>
            <p className='text-sm'>
              {releaseDestinationNote(preview.data.destination, 'pdf')}{' '}
              <span className='text-muted-foreground'>(from {preview.data.integrationName})</span>
            </p>
            <ProviderLine provider={preview.data.orderingProvider} />
            {pdf ? <PdfPreview document={preview.data.document} details={pdf.details} problem={pdf.problem} /> : null}
          </div>
        )}

        {send.error ? <p className='text-sm text-destructive'>{send.error.message}</p> : null}

        <DialogFooter>
          {sent ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button variant='outline' onClick={onClose} disabled={send.isPending}>
                Cancel
              </Button>
              <Button
                disabled={!preview.isSuccess || send.isPending || !pdf?.canSend}
                onClick={() => {
                  if (!tryClaim(sendLock)) return
                  send.mutate(
                    { mappingId: report.mappingId, productId, resultStatus },
                    { onSuccess: setSent, onSettled: () => releaseClaim(sendLock) },
                  )
                }}
              >
                {send.isPending ? <Loader2 className='mr-1 h-4 w-4 animate-spin' /> : null}
                Send to Epic
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
